import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { TextInput } from 'components/Inputs';
import { SearchResults } from 'components/SearchComponent/SearchResults';
import DateRangePickerInput from 'components/FormCreatorComponent/inputs/DateRangePickerInput';
import { fieldType } from 'components/FormCreatorComponent/types';
import MultipleSelectCheckmarks, { SelectedList } from 'components/FormElementsComponent/MultipleSelectCheckmarks';
import {
	WebhookMonitoringBusinessStatus,
	WebhookMonitoringOperationType,
	WebhookMonitoringProvider,
	WebhookMonitoringWebhookStatus,
	WebhookMonitoringBusinessStatusesByProvider,
	WebhookMonitoringOperationTypesByProvider,
} from 'constants/webhook-monitoring.constants';
import { generalTranslation } from 'constants/global.constants';
import { searchTypeEnum } from 'constants/dashboard.constants';
import { ReportInputFields } from 'constants/reports.constants';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { getSearchMatches } from 'services/dashboard.service';
import { getSearchResults, setSearchResults } from 'state/reducers/dashboard.reducer';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { isHebrewWord } from 'utils/index';
import { webhookMonitoringDashboardPanelSx } from './dashboardPanelSx';

const buildDateRangeField = (label: string, value: any) => ({
	type: fieldType.dateRangePicker,
	props: {
		required: false,
		name: ReportInputFields.DATE_RANGE,
		value: value?.from && value?.to ? { startDate: value.from, endDate: value.to } : '',
		label,
	},
});

type Props = {
	filters: Record<string, any>;
	setFilters: React.Dispatch<React.SetStateAction<Record<string, any>>>;
	includeWebhookStatus?: boolean;
};

export const MonitoringFilters = ({ filters, setFilters, includeWebhookStatus = false }: Props) => {
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const searchResults = useAppSelector(getSearchResults);
	const [companySearchInput, setCompanySearchInput] = useState('');
	const [clearStamp, setClearStamp] = useState(0);
	const isHebrewTerm = useMemo(() => isHebrewWord(companySearchInput), [companySearchInput]);

	const handleClear = () => {
		setFilters({});
		setClearStamp((n) => n + 1);
	};

	const COMPANY_SEARCH_DEBOUNCE_MS = 350;

	useEffect(() => {
		if (companySearchInput.length < 2) {
			dispatch(setSearchResults(null));
			return;
		}
		const handle = window.setTimeout(() => {
			dispatch(getSearchMatches({ searchWord: companySearchInput, searchType: searchTypeEnum.COMPANY_NAME }));
		}, COMPANY_SEARCH_DEBOUNCE_MS);
		return () => window.clearTimeout(handle);
	}, [companySearchInput, dispatch]);

	useEffect(() => {
		if (!filters.companyName) {
			setCompanySearchInput('');
			dispatch(setSearchResults(null));
		}
	}, [filters.companyName, dispatch]);

	const handleCompanySelect = (item: any) => {
		const name = isHebrewTerm ? item.hebrewName : item.name;
		setFilterField('companyName', name);
		setCompanySearchInput(name);
		dispatch(setSearchResults(null));
	};

	const setFilterField = (key: string, value: any) => {
		setFilters((prev: Record<string, any>) => {
			const next = { ...prev };
			let v = value;
			if (key === 'companyName' && typeof v === 'string') {
				v = v.trim();
			}
			if (v === '' || v === null || v === undefined || (Array.isArray(v) && v.length === 0)) {
				delete next[key];
			} else {
				next[key] = v;
			}
			return next;
		});
	};

	const pruneDependentFiltersForProviders = (
		next: Record<string, any>,
		providerValues: WebhookMonitoringProvider[]
	) => {
		if (!providerValues.length) {
			return next;
		}
		const allowedStatuses = new Set<string>(
			providerValues.flatMap((p) => WebhookMonitoringBusinessStatusesByProvider[p])
		);
		const allowedTypes = new Set<string>(
			providerValues.flatMap((p) => WebhookMonitoringOperationTypesByProvider[p])
		);
		const out = { ...next };
		if (out.businessStatus?.length) {
			const valid = out.businessStatus.filter((s: string) => allowedStatuses.has(s));
			if (valid.length !== out.businessStatus.length) {
				if (valid.length) {
					out.businessStatus = valid;
				} else {
					delete out.businessStatus;
				}
			}
		}
		if (out.operationType?.length) {
			const valid = out.operationType.filter((v: string) => allowedTypes.has(v));
			if (valid.length !== out.operationType.length) {
				if (valid.length) {
					out.operationType = valid;
				} else {
					delete out.operationType;
				}
			}
		}
		return out;
	};

	const handleMultiSelectChange = (key: string) => (list: SelectedList) => {
		const values = list.map((item) => item.value);
		if (key === 'provider') {
			const providerValues = values as WebhookMonitoringProvider[];
			setFilters((prev) => {
				const next = { ...prev };
				if (providerValues.length === 0) {
					delete next.provider;
					return next;
				}
				next.provider = providerValues;
				return pruneDependentFiltersForProviders(next, providerValues);
			});
			return;
		}
		setFilterField(key, values);
	};

	const setDateRange = (key: 'createdAt' | 'updatedAt', dates: any) => {
		setFilters((prev: Record<string, any>) => {
			const next = { ...prev };
			if (!dates?.startDate || !dates?.endDate) {
				delete next[key];
			} else {
				next[key] = { from: dates.startDate, to: dates.endDate };
			}
			return next;
		});
	};

	const selectedProviders: WebhookMonitoringProvider[] = filters.provider || [];

	const providerOptions: SelectedList = Object.values(WebhookMonitoringProvider).map((value) => ({
		value,
		name: t(`page.webhookMonitoring.provider.${value}`),
	}));

	const operationTypeOptions: SelectedList = useMemo(() => {
		let values = Object.values(WebhookMonitoringOperationType);
		if (selectedProviders.length > 0) {
			const allowed = new Set(selectedProviders.flatMap((p) => WebhookMonitoringOperationTypesByProvider[p]));
			values = values.filter((v) => allowed.has(v));
		}
		return values.map((value) => ({ value, name: generalTranslation[value] }));
	}, [selectedProviders]);

	const businessStatusOptions: SelectedList = useMemo(() => {
		let values = Object.values(WebhookMonitoringBusinessStatus);
		if (selectedProviders.length > 0) {
			const allowed = new Set(selectedProviders.flatMap((p) => WebhookMonitoringBusinessStatusesByProvider[p]));
			values = values.filter((v) => allowed.has(v));
		}
		return values.map((value) => ({ value, name: generalTranslation[value] }));
	}, [selectedProviders]);

	const webhookStatusOptions: SelectedList = Object.values(WebhookMonitoringWebhookStatus).map((value) => ({
		value,
		name: t(`page.webhookMonitoring.status.${value}`),
	}));

	const showCompanySearchResults = companySearchInput.length >= 2 && searchResults != null;

	return (
		<Box sx={webhookMonitoringDashboardPanelSx}>
			<Stack spacing={3}>
				<Stack direction="row" justifyContent="space-between" alignItems="center">
					<Box
						sx={{
							position: 'relative',
							width: '340px',
							'& .MuiOutlinedInput-notchedOutline': { borderRadius: '4px!important' },
						}}
					>
						<TextInput
							textFieldStyle={{
								'& .MuiOutlinedInput-root.Mui-focused img': {
									opacity: '1!important',
									filter: 'saturate(1)!important',
								},
							}}
							value={companySearchInput}
							placeholder={t('page.webhookMonitoring.field.companyName')}
							onChange={(event: any) => {
								setCompanySearchInput(event);
								if (!event) setFilterField('companyName', '');
							}}
							icon={
								<img
									src="/icons/search-2.svg"
									style={{
										position: 'absolute',
										margin: 0,
										top: '50%',
										right: '5px',
										transform: 'translate(0, -50%)',
										opacity: 0.5,
										filter: 'saturate(0.5)',
									}}
								/>
							}
						/>
						{showCompanySearchResults ? (
							<SearchResults
								searchResults={searchResults}
								searchType={searchTypeEnum.COMPANY_NAME}
								handleClick={handleCompanySelect}
								isHebrewTerm={isHebrewTerm}
							/>
						) : null}
					</Box>
					<Button variant="contained" onClick={handleClear}>
						{t('actions.name.clean')}
					</Button>
				</Stack>
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
					<Box sx={{ '& button': { minWidth: 150 } }}>
						<MultipleSelectCheckmarks
							key={`wm-provider-${clearStamp}`}
							label={t('page.webhookMonitoring.field.provider')}
							options={providerOptions}
							handleChange={handleMultiSelectChange('provider')}
							primaryLabel={t('label.all')}
						/>
					</Box>
					<Box sx={{ '& button': { minWidth: 150 } }}>
						<MultipleSelectCheckmarks
							key={`wm-operationType-${clearStamp}`}
							label={t('page.webhookMonitoring.field.operationType')}
							options={operationTypeOptions}
							handleChange={handleMultiSelectChange('operationType')}
							primaryLabel={t('label.all')}
						/>
					</Box>
					<Box sx={{ '& button': { minWidth: 150 } }}>
						<MultipleSelectCheckmarks
							key={`wm-businessStatus-${clearStamp}`}
							label={t('page.webhookMonitoring.field.businessStatus')}
							options={businessStatusOptions}
							handleChange={handleMultiSelectChange('businessStatus')}
							primaryLabel={t('label.all')}
						/>
					</Box>
					{includeWebhookStatus ? (
						<Box sx={{ '& button': { minWidth: 150 } }}>
							<MultipleSelectCheckmarks
								key={`wm-webhookStatus-${clearStamp}`}
								label={t('page.webhookMonitoring.field.webhookStatus')}
								options={webhookStatusOptions}
								handleChange={handleMultiSelectChange('webhookStatus')}
								primaryLabel={t('label.all')}
							/>
						</Box>
					) : null}
					<Box
						sx={{
							'& .MuiFilledInput-root': { borderRadius: '4px!important' },
							'& .MuiInputBase-root': {
								height: '52.4px!important',
								'& input': { padding: '0 12px 0 8px!important' },
							},
						}}
					>
						<DateRangePickerInput
							key={`wm-createdAt-${clearStamp}`}
							valid={true}
							cField={buildDateRangeField(t('page.webhookMonitoring.field.createdAt'), filters.createdAt)}
							initialValidation={[]}
							handleChange={undefined}
							sendSelectionToParent={(dates: any) => setDateRange('createdAt', dates)}
						/>
					</Box>
					<Box
						sx={{
							'& .MuiFilledInput-root': { borderRadius: '4px!important' },
							'& .MuiInputBase-root': {
								height: '52.4px!important',
								'& input': { padding: '0 12px 0 8px!important' },
							},
						}}
					>
						<DateRangePickerInput
							key={`wm-updatedAt-${clearStamp}`}
							valid={true}
							cField={buildDateRangeField(
								t('page.webhookMonitoring.filters.updatedAt'),
								filters.updatedAt
							)}
							initialValidation={[]}
							handleChange={undefined}
							sendSelectionToParent={(dates: any) => setDateRange('updatedAt', dates)}
						/>
					</Box>
				</Box>
			</Stack>
		</Box>
	);
};
